import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { dialog, safeJson, i18N } from '@/common/util'
import certificadosService from '@/services/certificados.service'
import { LoaderType } from '@/types/types'

export interface certificadoType {
  id?: string
  nome?: string
  tipo?: string
  jsonCertificado?: any
  createdAt?: string
  active?: boolean
  origem?: string
  certificadoOrigemId?: string
  imprimeVerso?: boolean
}

export const useCertificadosStore = defineStore('certificados', () => {
  const list = ref<certificadoType[]>([])
  const status = ref(0) // -1 erro de exceção; 0 - inicial; 1 - carregando; 2 - sucesso; 3 - erro não fatal

  const nStatus = ref({
    getAll: 'initial' as LoaderType
  })

  const loading = computed(() => status.value === 1 || nStatus.value.getAll === 'loading')

  const getById = async (id: string) => {
    status.value = 1
    try {
      const { success, data } = await certificadosService.get(id)
      if (success) data.jsonCertificado = JSON.parse(data.jsonCertificado)
      status.value = 2
      return { success, data }
    } catch (error) {
      status.value = -1
    }
  }

  const getAll = async () => {
    nStatus.value.getAll = 'loading'

    try {
      const { data, success } = await certificadosService.get()

      if (success) {
        list.value = (data || []).map((row) => ({
          ...row,
          jsonCertificado: safeJson(row.jsonCertificado) || {}
        }))

        nStatus.value.getAll = 'success'
        return
      }

      nStatus.value.getAll = 'error'
    } catch (error) {
      //
      nStatus.value.getAll = 'error'
    }
  }

  const create = async (objData: certificadoType) => {
    status.value = 1
    try {
      // O endpoint vai gerar isso aqui
      delete objData.id
      delete objData.createdAt

      const { data, success } = await certificadosService.create(
        {
          ...objData,
          jsonCertificado: JSON.stringify(objData.jsonCertificado)
        },
        'multipart'
      )
      if (success) {
        getAll()
        status.value = 2
        return data
      }
    } catch (error) {
      status.value = -1
    }

    return false
  }

  const update = async (objData: certificadoType) => {
    status.value = 1
    const { nome, tipo, active, jsonCertificado } = objData
    try {
      const { data, success } = await certificadosService.update<certificadoType>(
        objData.id,
        {
          nome,
          tipo,
          active,
          imprimeVerso: objData.imprimeVerso,
          jsonCertificado: JSON.stringify(jsonCertificado)
        },
        'multipart'
      )
      if (success) {
        const value = list.value.find(el => el.id === data.id)
        value && Object.assign(value, {
          ...data,
          jsonCertificado: JSON.parse(data.jsonCertificado)
        })
        status.value = 2
        return true
      }
    } catch (error) {
      status.value = -1
    }

    return false
  }

  const remove = async (certificado: certificadoType, force?: boolean) => {
    if (!force)
      return dialog.open({
        title: i18N('feedback.attention'),
        message: i18N('dialogs.delete.message', {type: i18N('labels.certificate').toLowerCase(), name: certificado.nome}),
        complete: () => remove(certificado, true)
      })

    try {
      status.value = 1
      const { success } = await certificadosService.remove(certificado.id)
      if (success) getAll()
      status.value = 2
      return success
    } catch (error) {
      status.value = -1
    }
  }

  const upload = async (objectData: { certificadoId: string; alias: string; data: any }) => {
    try {
      const { success, data } = await certificadosService.upload(objectData)
      if (success) getAll()
      return { success, data }
    } catch (error) {
      //
      return { success: false }
    }
  }

  const printCertificate = async (id: string, idCursoOuFormacao: string) => {
    status.value = 1
    try {
      const {data} = await certificadosService.imprimir(id, idCursoOuFormacao)
      return data
    } catch {
      //
    } finally {
    status.value = 2
    }
  }

  const printUserCertificate = async (id: string, idCursoOuFormacao: string, userId: string) => {
    status.value = 1
    try {
      const {data} = await certificadosService.imprimirCertificadoUsuario(id, idCursoOuFormacao, userId)
      return data
    } catch {
      //
    } finally {
    status.value = 2
    }
  }


  return {
    list,
    loading,
    status: nStatus.value,
    //
    getById,
    getAll,
    create,
    update,
    remove,
    upload,
    printCertificate,
    printUserCertificate
  }
})
